import { useCallback, useState } from 'react';
import { MergeTargetPicker } from 'src/components/MergeTargetPicker';
import { ModalContainer } from 'src/components/ModalContainer';
import { Client, useConversationParticipant } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { CLIENT_ICON, toClientSummary } from './client-summary';
import { useClientTargetSearch } from './use-client-target-search';

export const AssociateParticipantWithClientModal = () => {
  const { closeModal } = useModalMethods(Modals.AssociateParticipantWithClientModal);
  const { isOpened, modalProps } = useModalState(Modals.AssociateParticipantWithClientModal);
  const [clientTarget, setClientTarget] = useState<Client | null>(null);
  const { associateParticipantWithClient } = useConversationParticipant();
  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const {
    records,
    loading: searchLoading,
    hasMore,
    loadMore,
    setSearchTerm,
  } = useClientTargetSearch({ partnerId: modalProps?.partnerId });

  const closeAssociateModal = useCallback(() => {
    closeModal();
    setClientTarget(null);
    setSearchTerm('');
  }, [closeModal, setSearchTerm]);

  const handleAssociateParticipant = useCallback(async () => {
    if (!clientTarget?.clientId) {
      showErrorAlert(`Invalid client target ${clientTarget?.clientId}`);

      return;
    }

    if (!modalProps) {
      return;
    }

    const { participant, partnerId } = modalProps;

    closeAssociateModal();

    const response = await associateParticipantWithClient({
      participant,
      client: clientTarget,
      partnerId,
    });

    if (!response?.success) {
      return;
    }

    showSuccessAlert('Participant was associated with the client');
    setClientTarget(null);
  }, [
    associateParticipantWithClient,
    clientTarget,
    closeAssociateModal,
    modalProps,
    showErrorAlert,
    showSuccessAlert,
  ]);

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Merge into existing client"
      onClose={closeAssociateModal}
      actions={[
        { label: 'Cancel', kind: 'secondary' },
        {
          label: 'Merge into client',
          kind: 'primary',
          onClick: handleAssociateParticipant,
          disabled: !clientTarget,
          testId: 'associate-participant-submit',
        },
      ]}
      data-testid="associate-participant-modal"
    >
      <MergeTargetPicker
        label="Client"
        placeholder="Search clients"
        icon={CLIENT_ICON}
        records={records}
        target={clientTarget}
        toSummary={toClientSummary}
        onTargetChange={setClientTarget}
        onSearchChange={setSearchTerm}
        loading={searchLoading}
        hasMore={hasMore}
        loadMore={loadMore}
      />
    </ModalContainer>
  );
};
